import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { compact, isEmpty } from 'lodash';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ShowMore } from '@ebsco-ui/ebsco-ui';

import {
  AvailabilityStatus,
  HoldingAvailability,
  SectionWithTitle,
  ShowMoreButton,
} from '@app/components';
import { useMappingsContext } from '@app/contexts';
import { makeRequest } from '@app/utils';
import { sharedMessages } from '@app/translations';

import { StackMapDto, StackMapModal } from './StackMapModal';
import {
  getGroupedByLocationHoldingStatements,
  LibraryHoldings,
} from './LibraryHoldings';

import css from './AvailabilityTable.module.scss';

interface AvailabilityTableProps {
  records: HoldingAvailability[];
  maxAmountToDisplay: number;
}

export const AvailabilityTable: FC<AvailabilityTableProps> = ({
  records,
  maxAmountToDisplay,
}) => {
  const [isStackMapModalOpen, setIsStackMapModalOpen] = useState(true);
  const [stackMapModalData, setStackMapModalData] = useState<StackMapDto>();
  const { $t } = useIntl();
  const {
    configurations: { isStackMapEnabled, libraryLocationMap },
  } = useMappingsContext();
  const groupedHoldingStatements =
    getGroupedByLocationHoldingStatements(records);
  const hasHoldingStatements = !isEmpty(groupedHoldingStatements);

  const hasDueDate = records.some(record => record.dueDate);
  const hasLibraryName = records.some(record => record.library?.name);

  const stackMapQuery = useQuery(
    ['stackMap', records],
    () =>
      makeRequest<StackMapDto[]>({
        url: '/opac-tools/map',
        method: 'POST',
        data: records.map(record => ({
          callno: record.callNumber,
          library: record.library?.[libraryLocationMap.library],
          location: record.location[libraryLocationMap.location],
        })),
      }),
    {
      enabled:
        isStackMapEnabled &&
        Boolean(libraryLocationMap.location) &&
        Boolean(libraryLocationMap.library) &&
        hasLibraryName,
    }
  );

  const wrapCellContent = useCallback(
    ({ column, columnIndex, children, className = '', ...props }) => (
      <td
        data-label={column.label}
        className={cn(css.cell, className, {
          [css.withDueDate]: hasDueDate,
        })}
        key={columnIndex}
        {...props}
      >
        {children}
      </td>
    ),
    [hasDueDate]
  );

  const handleStackMapModalOpen = stackMapData => {
    setStackMapModalData(stackMapData);
    setIsStackMapModalOpen(true);
  };
  const columns = useMemo(
    () =>
      compact([
        {
          label: $t({
            id: 'holdingsTable.status',
            defaultMessage: 'Status:',
          }),
          formatter: (record, column, columnIndex) =>
            wrapCellContent({
              column,
              columnIndex,
              children: (
                <AvailabilityStatus
                  status={record.status}
                  hasCustomMessage
                  dueDate={record.dueDate}
                  loanType={
                    record.temporaryLoanType || record.permanentLoanType
                  }
                  holdCount={record.holdCount}
                />
              ),
            }),
        },
        hasLibraryName && {
          label: $t({
            id: 'holdingsTable.Library',
            defaultMessage: 'Library:',
          }),
          formatter: (record, column, columnIndex) =>
            wrapCellContent({
              column,
              columnIndex,
              children: record.library?.name,
            }),
        },
        {
          label: $t({
            id: 'holdingsTable.location',
            defaultMessage: 'Location:',
          }),
          formatter: (record, column, columnIndex) =>
            wrapCellContent({
              column,
              columnIndex,
              children: record.location.name,
            }),
        },
        {
          label: $t({
            id: 'holdingsTable.callNumber',
            defaultMessage: 'Call number:',
          }),
          headerRowProps: {
            colSpan: 3,
          },
          formatter: (record, column, columnIndex) => {
            const { callNumber = '', volume = '' } = record;
            const stackMapData = stackMapQuery.data?.find(
              stackMapRecord => stackMapRecord.callNo === callNumber
            );

            const hasStackMapData = Boolean(stackMapData?.mapurl);
            const callNumberCell = wrapCellContent({
              column,
              columnIndex,
              children: `${callNumber} ${volume}`,
              className: css.stackMapCell,
              colSpan: 2,
            });

            return (
              <Fragment key={columnIndex}>
                {callNumberCell}
                <td>
                  {isStackMapEnabled && hasStackMapData && (
                    <Button
                      className={css.mapItButton}
                      onClick={() => handleStackMapModalOpen(stackMapData)}
                    >
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      {$t({
                        id: 'holdingsTable.mapIt',
                        defaultMessage: 'Map it',
                      })}
                    </Button>
                  )}
                </td>
              </Fragment>
            );
          },
        },
      ]),
    [$t, hasLibraryName, wrapCellContent, stackMapQuery.data, isStackMapEnabled]
  );

  const getHeaderRow = () => {
    return (
      <tr className={css.row}>
        {columns.map((column, index) => (
          <th
            className={cn(css.headerCell)}
            key={index}
            scope="col"
            {...column.headerRowProps}
          >
            {column.label}
          </th>
        ))}
      </tr>
    );
  };

  const getBodyRows = () => {
    return records.map(record => (
      <tr
        className={css.row}
        key={record.id}
        data-auto="availability-table-content"
      >
        {columns.map((column, columnIndex) =>
          column.formatter(record, column, columnIndex)
        )}
      </tr>
    ));
  };

  const items = getBodyRows();

  return (
    <>
      {hasHoldingStatements && (
        <LibraryHoldings groupedHoldingStatements={groupedHoldingStatements} />
      )}
      <SectionWithTitle
        title={$t({
          id: 'instanceDetails.availabilityLocations',
          defaultMessage: 'Availability & locations',
        })}
      >
        <ShowMore limit={maxAmountToDisplay}>
          {stackMapModalData && (
            <StackMapModal
              isOpen={isStackMapModalOpen}
              stackMapData={stackMapModalData}
              onModalClose={() => setIsStackMapModalOpen(false)}
            />
          )}
          <div className={css.wrapper}>
            <table
              className={css.table}
              aria-label={$t(sharedMessages.libraryHoldings)}
            >
              <thead className={css.head}>{getHeaderRow()}</thead>
              <tbody className={css.body}>
                <ShowMore.Items>{items}</ShowMore.Items>
              </tbody>
            </table>
            <ShowMoreButton
              showMoreLimit={maxAmountToDisplay}
              dataLength={records.length}
              styleType="link"
              className={css.toggle}
            />
          </div>
        </ShowMore>
      </SectionWithTitle>
    </>
  );
};
